/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import {
    copyToClipboard, generateUUID, getCookie, getMode, getSetting, navigate, NumberString, roundTo, setCookie,
    setDefaultHeaders, showSnackbar, StrengthGauge
} from '@dvrd/dvr-controls';
import IDate from '@dvrd/idate';
import {CookieAttributes} from 'js-cookie';
import React from 'react';
import {BaseModel} from '../models/baseModel';
import User from '../models/userModel';
import {hasAdminLogin} from './adminUtil';
import {onRecommend2Fa} from './events';
import {
    DashboardPeriod, DashboardPeriods, ImageSources, OrderDirection, PeriodKey, SubscriptionStatus
} from './interfaces';
import {deleteFromLocal, getFromAppLocal, getFromLocal, saveToLocal} from './storageUtil';

export const getImg = (imgName: string, fallbackExtension: string = 'png'): ImageSources => {
    let webpPath: string | null = null;
    let fallbackPath: string = '/' + require(`../../res/img/${imgName}.${fallbackExtension}`);
    try {
        webpPath = '/' + require(`../../res/img/${imgName}.webp`);
    } catch {
    }
    const sources: Array<[string, string]> = [];
    if (webpPath) sources.push([webpPath, 'image/webp']);
    sources.push([fallbackPath, getFallbackImageType(fallbackExtension)]);
    return sources;
};

function getFallbackImageType(extension: string) {
    if (extension === 'png') return 'image/png';
    else if (/jpe?g/.test(extension)) return 'image/jpeg';
    else if (extension === 'webp') return 'image/webp';
    return '';
}

export const createErrorMessage = (message: string): string =>
    `${message} Probeer het later opnieuw of neem contact met ons op.`;

export const setDeviceId = () => {
    let deviceId = getCookie('bolmate-device-id');
    if (!deviceId) {
        deviceId = generateUUID();
        let options: CookieAttributes = {expires: 365};
        if (getSetting('mode') !== 'dev') {
            options.secure = true;
            options.sameSite = 'strict';
        }
        setCookie('bolmate-device-id', deviceId, options);
    }
    if (getMode() === 'dev')
        setDefaultHeaders({'X-Device-Id': deviceId});
};

export const getStrengthGauche = (): Array<StrengthGauge> => [
    {validator: (password: string) => password.length >= 8},
    {validator: (password: string) => /\d+/.test(password)},
    {validator: (password: string) => /[A-Z]+/.test(password)},
    {validator: (password: string) => /[!@#$%^&*()_+=\-\[\]{}|'";:/?.>,<`~]/.test(password)}
];

export const changeSearchParam = (key: string, value: string) => {
    const params = new URLSearchParams(location.search);
    params.set(key, value);
    history.replaceState({}, '', decodeURIComponent(`${location.pathname}?${params}`));
};

export const experimentalFeature = (content: React.ReactNode): React.ReactNode => {
    if (allowExperimental()) return content;
    return null;
};

export const allowExperimental = (): boolean => {
    return getSetting('allowExperimental') === true;
};

export const getToday = (withTime: boolean = true): IDate => {
    let now = IDate.now();
    if (!withTime) return now.startOf('day');
    return now;
};

export function userHasSubscription(user: User | null, allowTrial: boolean = true): boolean {
    if (!user?.subscription?.status) return false;
    const allowedStatuses = [SubscriptionStatus.ACTIVE, SubscriptionStatus.FREE];
    if (allowTrial) allowedStatuses.push(SubscriptionStatus.TRIAL);
    return allowedStatuses.includes(user?.subscription?.status);
}

export const warnTwoFactor = (user: User) => {
    if (!hasAdminLogin()) {
        if (user.getFlex('2fa_warned') === true || getFromAppLocal('newUser') === true) return;
        const warnAfter = new IDate(user.getFlex('warn_2fa_after'));
        if (IDate.now().isAtOrBefore(warnAfter, 'days')) return;
        user.setFlex('2fa_warned', true);
        user.update();
        if (user.twoFactorEnabled) return;
        onRecommend2Fa();
    }
};

export const LOCAL_STORAGE_DASHBOARD_PERIOD = 'bolmate-dashboard-period',
    LOCAL_STORAGE_PERIOD_START = 'bolmate-dashboard-period-start',
    LOCAL_STORAGE_PERIOD_END = 'bolmate-dashboard-period-end';

export const getDashboardPeriod = (periodKey?: PeriodKey, localStorageKey: string = LOCAL_STORAGE_DASHBOARD_PERIOD,
                                   localStorageStartKey: string = LOCAL_STORAGE_PERIOD_START,
                                   localStorageEndKey: string = LOCAL_STORAGE_PERIOD_END,
                                   allowNull: boolean = false): DashboardPeriod | null => {
    const key = periodKey ?? getFromLocal(localStorageKey);
    if (!key && allowNull) return null;
    let period: DashboardPeriod = DashboardPeriods[0];
    if (key)
        for (const pd of DashboardPeriods)
            if (pd.key === key) {
                period = pd;
            }
    if (period.key === PeriodKey.CUSTOM) {
        const periodStart = getFromLocal(localStorageStartKey),
            periodEnd = getFromLocal(localStorageEndKey);
        if (periodStart) period.periodStart = new IDate(periodStart);
        if (periodEnd) period.periodEnd = new IDate(periodEnd);
    }
    return period;
};

export const saveDashboardPeriod = (period: DashboardPeriod, localStorageKey: string = LOCAL_STORAGE_DASHBOARD_PERIOD,
                                    localStorageStartKey: string = LOCAL_STORAGE_PERIOD_START,
                                    localStorageEndKey: string = LOCAL_STORAGE_PERIOD_END) => {
    saveToLocal(localStorageKey, period.key);
    if (period.key === PeriodKey.CUSTOM) {
        if (period.periodStart)
            saveToLocal(localStorageStartKey, period.periodStart.format());
        if (period.periodEnd)
            saveToLocal(localStorageEndKey, period.periodEnd.format());
    } else {
        deleteFromLocal(localStorageStartKey);
        deleteFromLocal(localStorageEndKey);
    }
};

export function getQuarterFirstMonth(quarter: number) {
    return {
        1: 1,
        2: 4,
        3: 7,
        4: 10
    }[quarter]!;
}

export function getQuarterLastMonth(quarter: number) {
    return {
        1: 3,
        2: 6,
        3: 9,
        4: 12
    }[quarter]!;
}

export function setElementHighlight(selector: string, force?: boolean) {
    const element = document.querySelector(selector), parent = element?.parentElement;
    if (!element || !parent) return;
    const highlight = document.querySelector('div.highlight');
    if ((highlight && force === true) || (!highlight && force === false)) return;
    if (highlight) {
        element.classList.remove('highlighted');
        highlight.parentElement?.removeChild(highlight);
    } else {
        if (!element.classList.contains('highlighted'))
            element.classList.add('highlighted');
        const highlight = document.createElement('div');
        highlight.className = 'highlight';
        highlight.onclick = function() {
            setElementHighlight(selector);
        };
        parent.appendChild(highlight);
    }
}

export function ensureNegative(value: number | NumberString) {
    if (value instanceof NumberString) value = value.numberValue;
    if (value > 0) value *= -1;
    return value;
}

export function numberTrim(number: number, parseInitial: boolean = false) {
    if (number < 10000) return parseInitial ? parseMoney(number) : number.toString();
    if (number < 1000000) return `${Math.round(number / 1000)}k`;
    return `${(number / 1000000).toFixed(1).replace('.', ',')}M`;
}

export function onNavigate(route: string) {
    return function() {
        navigate(route);
    };
}

export function padNum(value: number, paddingCount: number = 1): string {
    const stripIndex = Math.max(value.toString().length, paddingCount + 1),
        paddedValue = '0'.repeat(paddingCount) + value;
    return paddedValue.substr(paddedValue.length - stripIndex);
}

export function replaceDateVariables(value: string, date?: IDate): string {
    if (!date) date = IDate.now();
    value = value.replace('{jaar}', date.year().toString());
    value = value.replace('{maand}', padNum(date.month()));
    value = value.replace('{dag}', padNum(date.day()));
    return value;
}

export function formatSimpleDate(value: IDate): string {
    const today = IDate.now();
    let dateFormat: string;
    if (value.year() === today.year()) dateFormat = 'DD MMM HH:mm';
    else dateFormat = 'DD MMM YYYY HH:mm';
    return value.format(dateFormat);
}

export function parseMoney(money: number, useThousandSeparator: boolean = true, dashWithZero: boolean = true,
                           keepDot: boolean = false): string {
    let moneyString: string;
    if ((money === 0 || money % 1 === 0) && dashWithZero) moneyString = money + ',-';
    else {
        moneyString = roundTo(money, 2);
        if (!keepDot) moneyString = moneyString.replace('.', ',');
    }
    if (/^0+[1-9]/.test(moneyString)) moneyString = moneyString.replace(/^0+/, '');
    if (useThousandSeparator) return parseThousandSeparator(moneyString);
    return moneyString;
}

export function parseThousandSeparator(value: string | number, separator: string = '.'): string {
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, separator);
}

export function getCurrentQuarter(): {start: IDate, end: IDate} {
    const today = IDate.now();
    const start = today.clone().startOf('quarter');
    const end = today.clone().endOf('quarter');
    return {start, end};
}

export function getCurrentMonth(): {start: IDate, end: IDate} {
    const today = IDate.now();
    return {start: today.clone().startOf('month'), end: today.clone().endOf('month')};
}

export function getCurrentYear(): {start: IDate, end: IDate} {
    const today = IDate.now();
    const start = today.clone().startOf('year');
    const end = today.clone().endOf('year');
    return {start, end};
}

export function downloadFilePath(fileName: string, downloadName: string) {
    if (fileName.length) {
        const url = require(`../../res/files/${fileName}`);
        downloadFileURL(url, downloadName);
    }
}

export function downloadFileURL(url: string, fileName: string) {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = fileName;
    anchor.target = '_blank';
    anchor.style.display = 'none';
    anchor.click();
}

export function formatInvoiceNumber(invoiceNum: number, numTemplate: string): string {
    const templateLength = numTemplate.length;
    if (invoiceNum.toString().length >= templateLength) return invoiceNum.toString();
    let zerosCount = 0;
    for (const char of numTemplate)
        if (char === '0') zerosCount++;
    const zeroPadded = '0'.repeat(zerosCount) + invoiceNum;
    const startIndex = Math.max(zeroPadded.length - templateLength, 0);
    return zeroPadded.substring(startIndex, startIndex + templateLength);
}

export function toPercentage(value: number, total: number, numDigits: number = 1) {
    return (value / total * 100).toFixed(numDigits);
}

export function orderDirectionFromInt(orderDirection: number | null | undefined): OrderDirection {
    if (orderDirection === null || orderDirection === undefined || orderDirection < 1) return OrderDirection.DESC;
    return OrderDirection.ASC;
}

export function deepClone(obj: any): any {
    if (obj === undefined || obj === null) return obj;
    else if (Array.isArray(obj)) return obj.map(deepClone);
    else if (obj instanceof Map) return new Map(obj);
    else if (obj.hasOwnProperty('clone')) {
        const cloneProp = obj.clone;
        if (typeof cloneProp === 'function') return obj.clone(false);
        return obj.clone;
    } else if (isObject(obj)) {
        const cloned: {[key: string]: any} = {};
        for (const [key, value] of Object.entries(obj)) cloned[key] = deepClone(value);
        return cloned;
    }
    return obj;
}

function isObject(val: any): boolean {
    return typeof val === 'object' && !Array.isArray(val) && val !== null && !(val instanceof BaseModel);
}

export function elementIsInView(element: HTMLElement) {
    const rect = element.getBoundingClientRect();
    return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    );
}

export function sum(...entries: Array<number>): number {
    return entries.reduce((prev: number, current: number) => prev + (Number.isNaN(current) ? 0 : current), 0);
}

export function renderPlural(count: number, single: string, plural: string) {
    if (count === 1) return single;
    return plural;
}

export function roundNumberString(value: NumberString, decimals: number = 2): NumberString {
    value.setValue(roundTo(value.numberValue, decimals));
    return value;
}


export function setsAreEqual(set1: Set<any>, ...sets: Array<Set<any>>) {
    return sets.every((set: Set<any>) => set1.size === set.size && [...set1].every((val: any) => set.has(val)));
}

export function onCopy(value: string, stopProp?: boolean, callback?: VoidFunction) {
    return function(evt: React.MouseEvent) {
        if (stopProp) evt.stopPropagation();
        copyToClipboard(value).then(() => {
            if (callback) callback();
            else showSnackbar(`'${value}' gekopieerd naar het klembord`);
        })
    }
}